import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db, saveLog } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar";
import { toast } from "sonner";

import {
  Wrench, RefreshCw, Search, 
  Save, Ban, Trash2, Plus, ShieldAlert
} from "lucide-react";

export default function AdminFunctions() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState([]);
  const [functionSearch, setFunctionSearch] = useState("");
  const [functionStatus, setFunctionStatus] = useState("all");
  const [newFunction, setNewFunction] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const normalizeFunctionName = (name = "") => {
    return String(name).toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ");
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const functionsSnap = await getDocs(collection(db, "functions"));
      setFunctions(functionsSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      toast.error("Erro ao carregar funções");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredFunctions = useMemo(() => {
    const searchText = normalizeFunctionName(functionSearch);
    return (functions || [])
      .filter((func) => {
        const nameMatch = normalizeFunctionName(func?.name || "").includes(searchText);
        const isActive = func?.active === true;
        const statusMatch = functionStatus === "all" || (functionStatus === "active" && isActive) || (functionStatus === "inactive" && !isActive);
        return nameMatch && statusMatch;
      })
      .sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "pt-BR"));
  }, [functions, functionSearch, functionStatus]);

  const activeCount = useMemo(() => functions.filter((f) => f?.active === true).length, [functions]);
  const inactiveCount = useMemo(() => functions.filter((f) => f?.active !== true).length, [functions]);

  const handleFunctionChange = (id, value) => {
    setFunctions((prev) => prev.map((func) => (func.id === id ? { ...func, name: value } : func)));
  };

  const handleCreateFunction = async (e) => {
    if (e) e.preventDefault(); // Impede o recarregamento da página ao dar Enter no form
    
    const name = newFunction.trim();
    if (!name || name.length < 3) return toast.error("Nome inválido ou muito curto");
    if (functions.some(f => normalizeFunctionName(f.name) === normalizeFunctionName(name))) return toast.error("Função já cadastrada");

    try {
      await addDoc(collection(db, "functions"), {
        name, active: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      });

      await saveLog(userProfile, "CRIAÇÃO DE FUNÇÃO", `Nova especialidade cadastrada: ${name.toUpperCase()}`);

      toast.success("Função criada!");
      setNewFunction("");
      loadData();
    } catch (e) { toast.error("Erro ao criar"); }
  };

  const handleUpdateFunction = async (func) => {
    const name = String(func?.name || "").trim();
    if (!name || name.length < 3) return toast.error("Nome inválido");
    try {
      await updateDoc(doc(db, "functions", func.id), { name, updatedAt: new Date().toISOString() });
      await saveLog(userProfile, "EDIÇÃO DE FUNÇÃO", `O nome da função ID ${func.id} foi alterado para ${name.toUpperCase()}`);

      toast.success("Atualizado!");
      loadData();
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const handleToggleFunction = async (func) => {
    try {
      const isActive = func?.active === true;
      await updateDoc(doc(db, "functions", func.id), { active: !isActive, updatedAt: new Date().toISOString() });
      await saveLog(userProfile, "STATUS DE FUNÇÃO", `A função ${func.name} foi ${isActive ? 'DESATIVADA' : 'REATIVADA'}.`);

      toast.success(isActive ? "Desativada" : "Ativada");
      loadData();
    } catch (e) { toast.error("Erro ao alterar status"); }
  };

  const handleDeleteFunction = async (id) => {
    const funcToDelete = functions.find(f => f.id === id);
    if (!window.confirm("Deseja excluir permanentemente?")) return;
    try {
      await deleteDoc(doc(db, "functions", id));
      await saveLog(userProfile, "EXCLUSÃO DE FUNÇÃO", `A função ${funcToDelete?.name || id} foi removida permanentemente do banco de dados.`);

      toast.success("Excluída");
      loadData();
    } catch (e) { toast.error("Erro ao excluir"); }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400">CARREGANDO...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="text-left">
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Funções & <span className="text-purple-600">Especialidades</span></h1>
              <div className="flex items-center gap-4 mt-4">
                 <button onClick={() => setFunctionStatus("all")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${functionStatus === 'all' ? 'bg-slate-900 text-white shadow-md' : 'bg-white text-slate-400 border border-slate-100'}`}>Todas ({functions.length})</button>
                 <button onClick={() => setFunctionStatus("active")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${functionStatus === 'active' ? 'bg-green-600 text-white shadow-md' : 'bg-white text-slate-400 border border-slate-100'}`}>Ativas ({activeCount})</button>
                 <button onClick={() => setFunctionStatus("inactive")} className={`text-[10px] font-black uppercase px-4 py-2 rounded-full transition-all ${functionStatus === 'inactive' ? 'bg-red-600 text-white shadow-md' : 'bg-white text-slate-400 border border-slate-100'}`}>Inativas ({inactiveCount})</button>
              </div>
            </div>
            
            <div className="relative w-full md:w-96 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
              <input 
                type="text" 
                placeholder="BUSCAR ESPECIALIDADE..." 
                className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none focus:ring-2 focus:ring-purple-500/20 transition-all" 
                value={functionSearch} 
                onChange={(e) => setFunctionSearch(e.target.value)} 
              />
            </div>
          </header>

          {/* ÁREA DE CADASTRO RÁPIDO - ENVOLVIDO EM FORM PARA ACEITAR ENTER */}
          <Card className="rounded-[2.5rem] border-none shadow-xl bg-white mb-10 overflow-hidden border-b-4 border-purple-500">
            <CardContent className="p-8">
              <form onSubmit={handleCreateFunction} className="flex flex-col md:flex-row items-center gap-4 text-left">
                <div className="flex-1 w-full relative">
                  <Plus className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                  <input 
                    className="w-full h-14 pl-12 pr-6 bg-slate-50 border-none rounded-2xl font-bold text-sm uppercase italic placeholder:text-slate-300 focus:bg-white transition-all outline-none" 
                    placeholder="NOME DA NOVA FUNÇÃO (EX: ELETRICISTA)" 
                    value={newFunction} 
                    onChange={(e) => setNewFunction(e.target.value)} 
                  />
                </div>
                <Button 
                  type="submit" 
                  className="h-14 px-8 bg-purple-600 hover:bg-purple-700 text-white font-black rounded-2xl uppercase italic text-[11px] gap-3 shadow-lg shadow-purple-200 active:scale-95 transition-all w-full md:w-auto"
                >
                  <Plus size={18} /> CADASTRAR FUNÇÃO
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="grid gap-4">
            {loading ? (
                <div className="py-20 text-center font-black text-slate-300 animate-pulse tracking-widest text-left uppercase">Sincronizando categorias...</div>
            ) : filteredFunctions.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200 uppercase font-black text-slate-300 italic">Nenhuma especialidade encontrada.</div>
            ) : filteredFunctions.map((func) => {
              const isActive = func?.active === true;
              return (
                <Card key={func.id} className={`rounded-[2rem] border-none shadow-xl transition-all ${!isActive ? 'bg-red-50/20' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-center gap-6 text-left">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${isActive ? 'bg-purple-50 text-purple-600' : 'bg-red-100 text-red-600'}`}>
                        <Wrench size={24} />
                      </div>
                      
                      <div className="flex-1 w-full text-left">
                        <div className="flex items-center gap-3 mb-2">
                           <input 
                             className={`bg-transparent border-none font-black text-xl uppercase italic tracking-tighter outline-none focus:text-purple-600 transition-colors w-full ${isActive ? 'text-slate-900' : 'text-slate-400'}`} 
                             value={func.name} 
                             onChange={(e) => handleFunctionChange(func.id, e.target.value)}
                             onKeyDown={(e) => e.key === 'Enter' && handleUpdateFunction(func)} // Atalho para salvar ao editar
                           />
                        </div>
                        <div className="flex items-center gap-4">
                           <Badge className={`border-none font-black text-[8px] uppercase px-3 py-1 italic tracking-widest ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                             {isActive ? "Operacional" : "Inativa"}
                           </Badge>
                           <span className="text-[10px] font-bold text-slate-400 uppercase italic">UID: {func.id.slice(0,8)}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                        <Button 
                          onClick={() => handleUpdateFunction(func)} 
                          className="flex-1 md:flex-none h-12 bg-slate-900 hover:bg-slate-800 text-white font-black rounded-xl uppercase italic text-[10px] gap-2"
                        >
                          <Save size={14} /> Salvar
                        </Button>
                        <Button 
                          onClick={() => handleToggleFunction(func)} 
                          variant="outline" 
                          className={`flex-1 md:flex-none h-12 rounded-xl font-black uppercase italic text-[10px] gap-2 border-slate-200 ${isActive ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'}`}
                        >
                          {isActive ? <Ban size={14} /> : <RefreshCw size={14} />} 
                          {isActive ? "Desativar" : "Ativar"}
                        </Button>
                        <Button 
                          onClick={() => handleDeleteFunction(func.id)} 
                          variant="destructive" 
                          className="h-12 w-12 rounded-xl flex items-center justify-center shadow-lg"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
}